import React, { useState, useEffect, useMemo, useId } from 'react';
import { Dialog } from 'primereact/dialog';
import surveyService from '../../services/data/survey.service';

const DEFAULT_LIKERT_OPTIONS = {
    '1': 'Discordo totalmente',
    '2': 'Discordo',
    '3': 'Neutro / Às vezes',
    '4': 'Concordo',
    '5': 'Concordo totalmente',
};

const emptyForm = () => ({
    text: '',
    question_type: 'NPS',
    anchor_low: 'Nunca',
    anchor_high: 'Sempre',
    likert_options: { ...DEFAULT_LIKERT_OPTIONS },
    dimension_id: '',
    factor_id: '',
    order: '',
    comment_optional: false,
    comment_required_if_low: false,
    is_engagement_question: false,
});

/**
 * Modal para adicionar pergunta a um template de pesquisa.
 */
export default function SurveyTemplateAddQuestionDialog({
    visible,
    onHide,
    companyId,
    templateId,
    dimensions = [],
    onCreated,
    onNotify,
}) {
    const uid = useId();
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (visible) {
            setForm(emptyForm());
            setSaving(false);
        }
    }, [visible]);

    const factorsForDimension = useMemo(() => {
        if (!dimensions.length || !form.dimension_id) return [];
        const d = dimensions.find((x) => x.id === form.dimension_id);
        return d?.factors || [];
    }, [dimensions, form.dimension_id]);

    function setLikertOption(key, value) {
        setForm(f => ({ ...f, likert_options: { ...f.likert_options, [key]: value } }));
    }

    function submit() {
        if (!companyId || !templateId) return;
        const text = form.text.trim();
        if (!text) {
            onNotify?.({ severity: 'warn', summary: 'Atenção', detail: 'Informe o texto da pergunta.' });
            return;
        }
        setSaving(true);
        const payload = {
            company_id: companyId,
            template_id: templateId,
            text,
            question_type: form.question_type,
            comment_optional: form.comment_optional,
            comment_required_if_low: form.comment_required_if_low,
            is_engagement_question: form.is_engagement_question,
        };
        if (form.question_type === 'NPS') {
            payload.anchor_low = form.anchor_low || 'Nunca';
            payload.anchor_high = form.anchor_high || 'Sempre';
        } else {
            payload.likert_options = form.likert_options;
        }
        if (form.dimension_id) payload.dimension_id = form.dimension_id;
        if (form.factor_id) payload.factor_id = form.factor_id;
        if (form.order !== '' && form.order !== null && !Number.isNaN(Number(form.order))) {
            payload.order = Number(form.order);
        }
        surveyService
            .createTemplateQuestion(payload)
            .then((created) => {
                onCreated?.(created);
                onHide?.();
            })
            .catch((err) => {
                const d = err.response?.data?.detail;
                const msg = Array.isArray(d) ? d.join(' ') : d || 'Falha ao criar pergunta';
                onNotify?.({ severity: 'error', summary: 'Erro', detail: msg });
            })
            .finally(() => setSaving(false));
    }

    const footer = (
        <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-outline-secondary btn-sm btn-rounded px-3" disabled={saving} onClick={onHide}>
                Cancelar
            </button>
            <button
                type="button"
                className="btn btn-success btn-sm btn-rounded px-3"
                disabled={saving || !form.text.trim()}
                onClick={submit}
            >
                {saving ? 'Salvando...' : 'Adicionar pergunta'}
            </button>
        </div>
    );

    return (
        <Dialog
            header={<span className="text-green">Nova pergunta</span>}
            visible={visible}
            onHide={onHide}
            footer={footer}
            style={{ width: 'min(640px, 95vw)' }}
            modal
            dismissableMask
            draggable={false}
        >
            <div className="mb-3">
                <label className="form-label small fw-bold">Tipo de pergunta</label>
                <div className="d-flex gap-3">
                    <div className="form-check">
                        <input
                            type="radio"
                            className="form-check-input"
                            id={`${uid}-type-nps`}
                            checked={form.question_type === 'NPS'}
                            onChange={() => setForm(f => ({ ...f, question_type: 'NPS' }))}
                        />
                        <label className="form-check-label" htmlFor={`${uid}-type-nps`}>
                            NPS (0–10)
                        </label>
                    </div>
                    <div className="form-check">
                        <input
                            type="radio"
                            className="form-check-input"
                            id={`${uid}-type-likert`}
                            checked={form.question_type === 'LIKERT'}
                            onChange={() => setForm(f => ({ ...f, question_type: 'LIKERT' }))}
                        />
                        <label className="form-check-label" htmlFor={`${uid}-type-likert`}>
                            Likert (1–5)
                        </label>
                    </div>
                </div>
            </div>

            <div className="mb-2">
                <label className="form-label small fw-bold">Texto da pergunta</label>
                <textarea
                    className="form-control form-control-sm"
                    rows={3}
                    value={form.text}
                    onChange={(e) => setForm({ ...form, text: e.target.value })}
                    placeholder="Digite a pergunta..."
                />
            </div>

            {form.question_type === 'NPS' ? (
                <div className="row g-2 mb-2">
                    <div className="col-md-6">
                        <label className="form-label small fw-bold">Rótulo do 0 (mínimo)</label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={form.anchor_low}
                            onChange={(e) => setForm({ ...form, anchor_low: e.target.value })}
                            maxLength={100}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label small fw-bold">Rótulo do 10 (máximo)</label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={form.anchor_high}
                            onChange={(e) => setForm({ ...form, anchor_high: e.target.value })}
                            maxLength={100}
                        />
                    </div>
                </div>
            ) : (
                <div className="mb-2">
                    <label className="form-label small fw-bold">Opções de resposta (editáveis)</label>
                    <div className="d-flex flex-column gap-1">
                        {['1', '2', '3', '4', '5'].map((key) => (
                            <div key={key} className="input-group input-group-sm">
                                <span className="input-group-text" style={{ minWidth: '32px' }}>{key}</span>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={form.likert_options[key] || ''}
                                    onChange={(e) => setLikertOption(key, e.target.value)}
                                    maxLength={100}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {dimensions.length > 0 && (
                <div className="row g-2 mb-2">
                    <div className="col-md-6">
                        <label className="form-label small fw-bold">Dimensão (opcional)</label>
                        <select
                            className="form-select form-select-sm"
                            value={form.dimension_id}
                            onChange={(e) => setForm({ ...form, dimension_id: e.target.value, factor_id: '' })}
                        >
                            <option value="">— Sem dimensão —</option>
                            {dimensions.map((d) => (
                                <option key={d.id} value={d.id}>{d.name}</option>
                            ))}
                        </select>
                    </div>
                    {factorsForDimension.length > 0 && (
                        <div className="col-md-6">
                            <label className="form-label small fw-bold">Fator (opcional)</label>
                            <select
                                className="form-select form-select-sm"
                                value={form.factor_id}
                                onChange={(e) => setForm({ ...form, factor_id: e.target.value })}
                            >
                                <option value="">— Sem fator —</option>
                                {factorsForDimension.map((f) => (
                                    <option key={f.id} value={f.id}>{f.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            )}

            <div className="row g-2 mb-2">
                <div className="col-md-4">
                    <label className="form-label small fw-bold">Ordem (opcional)</label>
                    <input
                        type="number"
                        min={0}
                        className="form-control form-control-sm"
                        placeholder="Automático"
                        value={form.order}
                        onChange={(e) => setForm({ ...form, order: e.target.value })}
                    />
                </div>
            </div>

            <div className="form-check form-check-inline small mb-2">
                <input
                    type="checkbox"
                    className="form-check-input"
                    id={`${uid}-co`}
                    checked={form.comment_optional}
                    onChange={(e) => setForm({ ...form, comment_optional: e.target.checked })}
                />
                <label className="form-check-label" htmlFor={`${uid}-co`}>Comentário opcional</label>
            </div>
            <div className="form-check form-check-inline small mb-2">
                <input
                    type="checkbox"
                    className="form-check-input"
                    id={`${uid}-cr`}
                    checked={form.comment_required_if_low}
                    onChange={(e) => setForm({ ...form, comment_required_if_low: e.target.checked })}
                />
                <label className="form-check-label" htmlFor={`${uid}-cr`}>
                    {form.question_type === 'LIKERT' ? 'Exigir comentário se nota ≤ 2' : 'Exigir comentário se nota ≤ 6'}
                </label>
            </div>
            <div className="form-check small mb-0">
                <input
                    type="checkbox"
                    className="form-check-input"
                    id={`${uid}-enps`}
                    checked={form.is_engagement_question}
                    onChange={(e) => setForm({ ...form, is_engagement_question: e.target.checked })}
                />
                <label className="form-check-label" htmlFor={`${uid}-enps`}>Pergunta de engajamento (eNPS)</label>
            </div>
        </Dialog>
    );
}
